import { Injectable } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { RtPlatform } from '@recruitee/user-interface';

import { WysiwygShortcuts } from './consts';

interface Translations {
  'accessibility-help-label': string;
  align: string;
  'align-center': string;
  'align-justify': string;
  'align-left': string;
  'align-right': string;
  'bold-abbr': string;
  cancel: string;
  caption: string;
  center: string;
  close: string;
  code: string;
  bold: string;
  delete: string;
  bulletslist: string;
  deleted: string;
  'deleted-abbr': string;
  edit: string;
  file: string;
  filename: string;
  fontcolor: string;
  fontfamily: string;
  'remove-font-family': string;
  format: string;
  fullscreen: string;
  heading1: string;
  heading2: string;
  heading3: string;
  heading4: string;
  heading5: string;
  heading6: string;
  highlight: string;
  horizontalrule: string;
  image: string;
  'image-position': string;
  indent: string;
  insert: string;
  'italic-abbr': string;
  left: string;
  'link-edit': string;
  'link-in-new-tab': string;
  'link-insert': string;
  lists: string;
  italic: string;
  none: string;
  optional: string;
  link: string;
  orderedlist: string;
  outdent: string;
  paragraph: string;
  quote: string;
  'remove-size': string;
  numberslist: string;
  right: string;
  save: string;
  size: string;
  subscript: string;
  'subscript-abbr': string;
  redo: string;
  superscript: string;
  'superscript-abbr': string;
  text: string;
  title: string;
  underline: string;
  'underline-abbr': string;
  unlink: string;
  unorderedlist: string;
  upload: string;
  'upload-change-label': string;
  'upload-label': string;
  video: string;
  undo: string;
  'video-html-code': string;
}

@Injectable({ providedIn: 'root' })
export class RedactorTranslations {
  public translations: Translations;
  public locale = this.i18n.locale;

  private metaKey = this.platform.OSX ? '⌘' : 'ctrl';

  constructor(private platform: RtPlatform, private i18n: I18nService) {
    this.i18n.localeChanged.subscribe(locale => {
      this.locale = locale;
      this.translate();
    });
  }

  // eslint-disable-next-line max-lines-per-function
  private translate() {
    const t = this.i18n.translate.bind(this.i18n);

    this.translations = {
      'accessibility-help-label': t('wysiwyg.accessibility-help-label'),
      'align-center': t('wysiwyg.align-center'),
      'align-justify': t('wysiwyg.align-justify'),
      'align-left': t('wysiwyg.align-left'),
      'align-right': t('wysiwyg.align-right'),
      'bold-abbr': t('wysiwyg.bold-abbr'),
      'deleted-abbr': t('wysiwyg.deleted-abbr'),
      'image-position': t('wysiwyg.image-position'),
      'italic-abbr': t('wysiwyg.italic-abbr'),
      'link-edit': t('wysiwyg.link-edit'),
      'link-in-new-tab': t('wysiwyg.link-in-new-tab'),
      'link-insert': t('wysiwyg.link-insert'),
      'remove-font-family': t('wysiwyg.remove-font-family'),
      'remove-size': t('wysiwyg.remove-size'),
      'subscript-abbr': t('wysiwyg.subscript-abbr'),
      'superscript-abbr': t('wysiwyg.superscript-abbr'),
      'underline-abbr': t('wysiwyg.underline-abbr'),
      'upload-change-label': t('wysiwyg.upload-change-label'),
      'upload-label': t('wysiwyg.upload-label'),
      'video-html-code': t('wysiwyg.video-html-code'),
      align: t('wysiwyg.align'),
      bold: `${t('wysiwyg.bold')} ${WysiwygShortcuts.Bold(this.metaKey)}`,
      bulletslist: `${t('wysiwyg.bulletslist')} ${WysiwygShortcuts.Bulletslist(this.metaKey)}`,
      cancel: t('wysiwyg.cancel'),
      caption: t('wysiwyg.caption'),
      center: t('wysiwyg.center'),
      close: t('wysiwyg.close'),
      code: t('wysiwyg.code'),
      delete: t('wysiwyg.delete'),
      deleted: t('wysiwyg.deleted'),
      edit: t('wysiwyg.edit'),
      file: t('wysiwyg.file'),
      filename: t('wysiwyg.filename'),
      fontcolor: t('wysiwyg.fontcolor'),
      fontfamily: t('wysiwyg.fontfamily'),
      format: t('wysiwyg.format'),
      fullscreen: t('wysiwyg.fullscreen'),
      heading1: t('wysiwyg.heading1'),
      heading2: t('wysiwyg.heading2'),
      heading3: t('wysiwyg.heading3'),
      heading4: t('wysiwyg.heading4'),
      heading5: t('wysiwyg.heading5'),
      heading6: t('wysiwyg.heading6'),
      highlight: t('wysiwyg.highlight'),
      horizontalrule: t('wysiwyg.horizontalrule'),
      image: t('wysiwyg.image'),
      indent: t('wysiwyg.indent'),
      insert: t('wysiwyg.insert'),
      italic: `${t('wysiwyg.italic')} ${WysiwygShortcuts.Italic(this.metaKey)}`,
      left: t('wysiwyg.left'),
      link: `${t('wysiwyg.link')} ${WysiwygShortcuts.Link(this.metaKey)}`,
      lists: t('wysiwyg.lists'),
      none: t('wysiwyg.none'),
      numberslist: `${t('wysiwyg.numberslist')} ${WysiwygShortcuts.Numberslist(this.metaKey)}`,
      optional: t('wysiwyg.optional'),
      orderedlist: t('wysiwyg.orderedlist'),
      outdent: t('wysiwyg.outdent'),
      paragraph: t('wysiwyg.paragraph'),
      quote: t('wysiwyg.quote'),
      redo: `${t('wysiwyg.redo')} ${WysiwygShortcuts.Redo(this.metaKey)}`,
      right: t('wysiwyg.right'),
      save: t('wysiwyg.save'),
      size: t('wysiwyg.size'),
      subscript: t('wysiwyg.subscript'),
      superscript: t('wysiwyg.superscript'),
      text: t('wysiwyg.text'),
      title: t('wysiwyg.title'),
      underline: `${t('wysiwyg.underline')} ${WysiwygShortcuts.Underline(this.metaKey)}`,
      undo: `${t('wysiwyg.undo')} ${WysiwygShortcuts.Undo(this.metaKey)}`,
      unlink: t('wysiwyg.unlink'),
      unorderedlist: t('wysiwyg.unorderedlist'),
      upload: t('wysiwyg.upload'),
      video: t('wysiwyg.video'),
    };
  }
}
